// ─────────────────────────────────────────────────────────────────────────────
// stores/companyStore.ts
// Verwaltet alle Firmen und die aktuell gewählte Firma (Context)
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import {
  collection, addDoc, updateDoc,
  deleteDoc, doc, getDoc, serverTimestamp, query, orderBy,
  setDoc,
  type Unsubscribe, type QueryConstraint, type QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { liveAbo } from '@/utils/liveAbo';
import { mitAnspruchsWiederholung } from '@/utils/anspruch';
import { diagnoseSessionDecision } from '@/utils/diagnose';
import { useAuthStore, BOOTSTRAP_TIMEOUT_MS } from './authStore';
import type { Company, CompanyMember } from '@/firebase/models';

/**
 * DAS VERDIKT UEBER DIE GESPEICHERTE MANDANTENWAHL (T-M34.4, 2026-09-08 —
 * REQ-OPS-058 Praez. 2 (a)/(b)).
 *
 * Genau EIN Verdikt bedeutet „weiterfahren"; die drei Rueckfallfaelle der
 * Festlegung (a) und der Fristablauf aus (c) sind je EIGENS benannt — nicht,
 * weil der Waechter sie verschieden behandelte (er fuehrt in allen vier Faellen
 * auf `/select-company`), sondern weil das Diagnose-Protokoll sie
 * unterscheidbar melden muss (REQ-OPS-055 (b), Klasse Sitzungsentscheide).
 * *Ein gemeinsames `false` saehe im Feld bei allen vier Ursachen gleich aus.*
 */
export type MandantenwahlVerdikt =
  | 'bestaetigt'
  | 'keine-kennung'
  | 'nicht-im-anspruch'
  | 'nicht-lesbar'
  | 'frist-abgelaufen';

/**
 * Ein Vorgang mit Frist — DIESELBE BAUFORM WIE `warteAufAnmeldestart`
 * (REQ-OPS-052 (b)/(e), REQ-OPS-058 Praez. 2 (c), T-M34.4).
 *
 * Ein Promise, ein `setTimeout`, ein Wettlauf; laeuft die Frist ab, meldet der
 * Aufrufer `abgelaufen` und faellt in seinen Rueckfall — er wartet NIE laenger.
 * **Die Zahl steht nicht hier:** sie kommt als `BOOTSTRAP_TIMEOUT_MS` aus dem
 * Auth-Store. Eine zweite Zahl waeren zwei Wahrheiten, und die Frist ist
 * ausdruecklich nur EINMAL gefuehrt (REQ-OPS-052 (b)).
 *
 * Der Zeitgeber wird in JEDEM Ausgang abgeraeumt — auch im Erfolgsfall: ein
 * stehengelassener Zeitgeber haelt unter gestellter Zeit den Lauf auf und im
 * Betrieb den Ereignisverlauf wach.
 */
async function mitFrist<T>(
  arbeit:  Promise<T>,
  fristMs: number,
): Promise<{ abgelaufen: true } | { abgelaufen: false; wert: T }> {
  let zeitgeber: ReturnType<typeof setTimeout> | undefined;
  const frist = new Promise<{ abgelaufen: true }>((resolve) => {
    zeitgeber = setTimeout(() => resolve({ abgelaufen: true }), fristMs);
  });
  try {
    return await Promise.race([
      arbeit.then((wert) => ({ abgelaufen: false as const, wert })),
      frist,
    ]);
  } finally {
    clearTimeout(zeitgeber);
  }
}

/**
 * Abfrageform der Mandantenliste (REQ-DM-050/050a, REQ-OPS-058 (1) und (3)).
 *
 * Form: KEIN Filter (die Mandantenwahl ist von Natur aus
 * mandantenuebergreifend — sie IST die Wahl des Mandanten und kann deshalb
 * keinen `companyId`-Filter tragen; REQ-OPS-032 Praez. 1 richtet sich an die
 * mandantenGEBUNDENEN Listen, allen voran die Druckerliste), Sortierung nach
 * `name` aufsteigend, `__name__` als impliziter Tiebreak. Ohne
 * Gleichheitsfilter genuegt der automatische Einzelfeld-Index (belegt in
 * `test/firestoreIndexes.test.ts`, REQ-DM-050a).
 *
 * ⚠ BERICHTIGT AM 2026-08-22 (T-M24.3, REQ-OPS-036 Praez. 6 — berichtigt statt
 * geloescht). WORTLAUT BIS DAHIN:
 *   „Gemeinsame Abfrageform der Mandantenliste — EINE Stelle fuer die
 *    Live-Seite und fuer das Nachladen (REQ-DM-050/050a, T-M8.19b). […]
 *    `startAfter` aendert daran nichts."
 * ES GIBT KEINE ZWEITE SEITE MEHR: der Auftraggeber hat am 2026-08-19
 * entschieden, dass jederzeit ALLE Firmen zur Auswahl bereitstehen
 * („Das inkrementelle Laden muss weg!"), und damit REQ-OPS-032 fuer diese eine
 * Liste aufgehoben (REQ-OPS-058). Die Abfrage traegt seither **kein `limit`**
 * und **kein `startAfter`**. Fuer die Druckerliste gilt REQ-OPS-032
 * unveraendert weiter — `printerStore` blaettert wie bisher.
 */
function companiesQueryConstraints(): QueryConstraint[] {
  return [orderBy('name')];
}

export const useCompanyStore = defineStore('company', () => {
  // ── State ──────────────────────────────────────────────────
  // ⚠ BERICHTIGT AM 2026-08-22 (T-M24.3, REQ-OPS-058). WORTLAUT BIS DAHIN:
  //   „Seiten statt einer gekappten Momentaufnahme (REQ-OPS-032 in der Fassung
  //    vom 2026-08-17, T-M8.19b) — Bauform wie im printerStore und in der
  //    Auftragsliste: live ist die erste Seite, nachgeladene stehen still."
  // DIE SEITEN SIND WEG: EINE Momentaufnahme haelt ALLE Firmen (REQ-OPS-058
  // (1)). Damit entfallen der Cursor, die stillstehenden Folgeseiten und das
  // Zusammenfuehren — nichts davon hat noch einen Gegenstand. Die
  // Druckerliste behaelt ihre Bauform unveraendert (REQ-OPS-058 (5)).
  const companies         = ref<Company[]>([]);
  const isLoading         = ref(false);
  const error             = ref<string | null>(null);
  const members           = ref<CompanyMember[]>([]);
  const membersLoading    = ref(false);
  /**
   * Haelt der Store gerade einen Listener (REQ-OPS-053 (a) (ii), OP-151)?
   *
   * Zwei Abos, zwei Merkmale — die Mandantenliste und die Mitgliederliste eines
   * Mandanten sterben unabhaengig voneinander, und ein gemeinsames Merkmal
   * behauptete fuer das eine, was fuer das andere gilt.
   */
  const isSubscribed        = ref(false);
  const isMembersSubscribed = ref(false);
  /**
   * LIEGT FUER DAS LAUFENDE ABO EINE ANTWORT VOR — gegeben NACH dem gelesenen
   * Anspruch (T-M47.2, REQ-OPS-058 Praez. 4 (c))?
   *
   * `isLoading` allein genuegt dafuer nicht: es steht vor dem ersten
   * `subscribe()` auf `false`, und eine leere Liste mit `isLoading === false`
   * liest die Firmen-Auswahl als „Noch keine Firmen angelegt.". **Erst dieses
   * Merkmal macht aus der leeren Liste eine Aussage**; bis dahin zeigt die
   * Auswahl ihren Ladezustand. Gesetzt wird es von der Antwort des Abos
   * (Daten, Fehler oder Stillstand) bzw. vom Ende der Einzellesung, zurueck
   * faellt es mit jedem neuen `subscribe()` und mit `unsubscribe()`.
   */
  const antwortLiegtVor     = ref(false);

  // ⚠ ENTFALLEN AM 2026-08-22 (T-M24.3, REQ-OPS-058 (2)): `hasMore`,
  // `limitReached`, `loadedPages`, `hasStaticPages`, `isLoadingMore`,
  // `loadMoreError` und `loadMore()`. Sie haben keinen Gegenstand mehr — es
  // gibt keine zweite Seite, also auch keine Kuerzung, kein Nachladen und
  // keinen Nachladefehler. *Bewusst ersatzlos statt als stehengebliebene
  // Attrappe: ein Aufrufer, der weiterblaettern will, bricht damit in der
  // Typpruefung, statt still gegen ein `false` zu laufen.* Die
  // gleichnamigen Merkmale des `printerStore` bleiben unveraendert bestehen
  // (REQ-OPS-032 gilt fuer die Druckerliste weiter).

  // Persist selected company ID in localStorage so it survives page reload
  const selectedCompanyId  = ref<string | null>(
    localStorage.getItem('pf_company_id'),
  );

  let _unsub:       Unsubscribe | null = null;
  let _memberUnsub: Unsubscribe | null = null;

  /**
   * WELCHES der zwei Abos die stehende Meldung geschrieben hat.
   *
   * `error` ist EIN Feld, an dem zwei Abos haengen. Ohne dieses Merkmal naehme
   * die Erholung des einen dem anderen die Meldung weg — der Rahmen-Hinweis
   * (T-M21.5) verschwaende dann, obwohl die Mitgliederliste noch tot ist.
   * Zwei getrennte Meldungsfelder waeren die groessere Aenderung: `error` wird
   * an mehreren Stellen der Oberflaeche gelesen.
   */
  let _fehlerQuelle: 'companies' | 'members' | null = null;

  // ── Getters ────────────────────────────────────────────────
  const selectedCompany = computed<Company | null>(
    () => companies.value.find(c => c.id === selectedCompanyId.value) ?? null,
  );

  const hasCompany = computed(() => !!selectedCompanyId.value && !!selectedCompany.value);

  // ── Subscribe to Firestore ─────────────────────────────────
  /**
   * Listenzustand zuruecksetzen — kein Nachhall aus einem frueheren Abo.
   *
   * Zusammen mit `isLoading = true` ist das die Regel aus REQ-OPS-032
   * Praez. 2, die REQ-OPS-058 ausdruecklich unberuehrt laesst: zwischen
   * erneutem Abonnieren und der ersten Antwort steht ein LADEZUSTAND, keine
   * Liste von gestern.
   */
  function resetListe(): void {
    companies.value = [];
  }

  /**
   * DIE MANDANTENWAHL WIRD GEGEN DEN ANSPRUCH GEPRUEFT (T-M30.7,
   * REQ-SEC-028 (a), REQ-DM-070 (e)).
   *
   * `pf_company_id` ist eine SITZUNGSANGABE im `localStorage` und keine
   * Eigenschaft des Benutzers — sie ueberlebt jede Anmeldung, auch die eines
   * anderen Benutzers am selben Geraet. Liegt der gewaehlte Mandant nicht im
   * Anspruch, faellt die Wahl zurueck: `clearCompany()` nimmt sie samt Eintrag
   * zurueck, und der Router-Waechter fuehrt mangels Mandant zur Auswahl.
   *
   * **Ein Admin darf jede Kennung** (REQ-SEC-027 (a)); geprueft wird nur der
   * `user`. Ohne gelesenen Anspruch ist die Antwort `false` — die strenge
   * Richtung; die Wahl faellt dann zurueck statt eine Ansicht zu oeffnen, die
   * die Rules ohnehin abweisen.
   *
   * ⚠ **GEPRUEFT WIRD ERST, WENN DER ANSPRUCH GELESEN IST** (T-M34.10,
   * Nachbesserung zu T-M34.8, 2026-09-08 — REQ-OPS-058 Praez. 2 (g)/(g1)). *Berichtigt statt
   * geloescht (REQ-OPS-036 Praez. 6): bis dahin lief die Pruefung bei JEDEM
   * Aufruf, auch vor dem gelesenen Anspruch.* **Die Strenge selbst ist Zeichen
   * fuer Zeichen dieselbe geblieben** — `darfMandant` antwortet ohne Anspruch
   * unveraendert `false` (REQ-SEC-028 (a)), und eine Kennung, die ein GELESENER
   * Anspruch nicht traegt, faellt wie bisher zurueck. Geaendert ist allein, ob
   * in diesem Zustand ueberhaupt zurueckgenommen wird: **ohne gelesenen
   * Anspruch wird nicht geprueft und nichts zurueckgenommen** (Wortlaut der
   * Festlegung (g)). **Es entsteht dadurch KEIN milderer Zwischenzustand** —
   * die Wahl gilt bis dahin nicht als bestaetigt, sie wird nur nicht verworfen;
   * `bestaetigeGespeicherteWahl` faehrt danach unveraendert durch ihre vier
   * Ausgaenge.
   *
   * **Der gemessene Anlass** (Feldbefund 4 der Feldprobe T-M34.7,
   * `printerque-test`, tab-uebergreifend): an einer BILDSCHIRM-Adresse liest
   * der Anmelde-Start den Anspruch absichtlich nie (`amPortal === false`),
   * beendet aber regulaer. Eine im selben Browser geoeffnete Portal-Sitzung
   * verlor darueber ihre Firmenwahl, weil `localStorage` dem URSPRUNG gehoert
   * und nicht dem Tab. Die zweite, vorgelagerte Massnahme steht in `App.vue`
   * (das Firmen-Abo startet nur an Portal-Pfaden); **diese hier ist die
   * Sperre am Ort der Entscheidung** — sie haelt auch jeden kuenftigen
   * Aufrufer, der `pruefeMandantenwahl` direkt ruft.
   */
  function pruefeMandantenwahl(): void {
    const authStore = useAuthStore();
    const gewaehlt  = selectedCompanyId.value;
    if (!gewaehlt) return;
    if (!authStore.anspruchGelesen) return;
    if (authStore.darfMandant(gewaehlt)) return;
    clearCompany();
  }

  /**
   * Die in dieser Sitzung bereits bestaetigte Kennung — der Merkposten, der den
   * gezielten Lesevorgang auf EINEN je Wahl begrenzt.
   *
   * ⚠ **KEIN ZUSTAND DER OBERFLAECHE und kein Ersatz fuer die Pruefung:** der
   * Anspruch wird auch bei bestaetigter Wahl bei JEDEM Aufruf erneut geprueft
   * (REQ-OPS-058 Praez. 2 (e)); gespart wird allein das wiederholte Lesen
   * desselben Dokuments bei jeder Navigation. Absichtlich KEINE `ref`: nichts
   * rendert ihn, und ein reaktiver Wert luede dazu ein, ihn als „ist gewaehlt"
   * zu lesen — das ist `selectedCompanyId` und bleibt es.
   *
   * `selectCompany`/`clearCompany` ruehren ihn NICHT an (REQ-OPS-058 Praez. 2
   * (d): beide bleiben Zeichen fuer Zeichen). Eine andere Kennung als die
   * gemerkte fuehrt ohnehin in den Lesevorgang.
   */
  let _bestaetigteWahl: string | null = null;

  /** Eine Zeile fuer den Entscheid (REQ-OPS-055 (b), Klasse Sitzungsentscheide). */
  function meldeVerdikt(verdikt: MandantenwahlVerdikt, companyId: string | null): MandantenwahlVerdikt {
    diagnoseSessionDecision(
      `companyChoice.${verdikt}`,
      { companyId, verdict: verdikt, source: 'router' },
      verdikt === 'bestaetigt' ? 'info' : 'warn',
    );
    return verdikt;
  }

  /**
   * DIE GETROFFENE FIRMENWAHL HAELT DEN NEUSTART (T-M34.4, 2026-09-08 —
   * REQ-OPS-058 Praez. 2, OP-167 Weg (ii)).
   *
   * ⚠ **BIS ZUM 2026-09-08 ENTSCHIED DER ROUTER-WAECHTER UEBER `hasCompany`,
   * ALSO AUS DER GELADENEN LISTE.** Die Liste kommt aber erst nach der
   * Anmeldung (`App.vue` abonniert sie dort), waehrend `pf_company_id` im
   * `localStorage` ueberlebt: nach JEDEM Neuladen gewann der Waechter das
   * Rennen gegen das Abo und schickte den Admin auf `/select-company`.
   * *Berichtigt statt geloescht — REQ-OPS-036 Praez. 6.*
   *
   * **DIE ZUGEHOERIGKEIT WIRD AUS KENNUNG PLUS ANSPRUCH GEBILDET** (Festlegung
   * (b)), und das Dokument wird **gezielt einzeln** gelesen — `getDoc` ueber
   * {@link fetchCompanyById}. Kein zweites Abo, keine Abfrage, kein Nachladen
   * der Liste: die Rules erlauben `companies/{id}` einem Mitglied ohnehin nur
   * als `get` (REQ-SEC-029 (e)).
   *
   * Die vier Ausgaenge, die auf die Auswahl fuehren, sind abschliessend
   * (Festlegung (a) plus die Frist aus (c)):
   *   • `keine-kennung`     — nichts gespeichert.
   *   • `nicht-im-anspruch` — `pruefeMandantenwahl` hat die Wahl zurueckgenommen
   *     (streng: der GELESENE Anspruch traegt die Kennung nicht, REQ-SEC-028 (a);
   *     ein Admin darf jede Kennung, REQ-SEC-027 (a)). *Der Klammerzusatz lautete
   *     bis zum 2026-09-08 „ohne gelesenen Anspruch `false`" — berichtigt statt
   *     geloescht (REQ-OPS-036 Praez. 6): `darfMandant` antwortet unveraendert
   *     `false`, aber `pruefeMandantenwahl` nimmt VOR dem gelesenen Anspruch
   *     nichts mehr zurueck (REQ-OPS-058 Praez. 2 (g)/(g1), T-M34.10). **Am Portal
   *     aendert das nichts** — der Waechter laeuft erst nach dem aufgeloesten
   *     Anmeldestart, und dort IST der Anspruch gelesen (`amPortal === true`).*
   *   • `nicht-lesbar`      — das Dokument fehlt oder die Rules weisen es ab.
   *     **Das ist KEINE Aussage ueber die Rules** (Festlegung (f)): ein vom
   *     Anspruch getragener, aber abgewiesener Mandant fuehrt auf die AUSWAHL
   *     und nicht in die Fehleransicht des Anmeldestarts.
   *   • `frist-abgelaufen`  — der Lesevorgang antwortet nicht innerhalb der
   *     Frist des Anmeldestarts. **Nie ein stehendes Ladebild** (Festlegung
   *     (c)); sonst entstuende genau die zweite Haengestelle, die
   *     REQ-OPS-052 (e) beseitigt hat.
   *
   * **In jedem anderen Fall faehrt der Waechter an die angeforderte Adresse.**
   */
  async function bestaetigeGespeicherteWahl(
    fristMs: number = BOOTSTRAP_TIMEOUT_MS,
  ): Promise<MandantenwahlVerdikt> {
    const gewaehlt = selectedCompanyId.value;
    if (!gewaehlt) return meldeVerdikt('keine-kennung', null);

    // Der Anspruch entscheidet, und er entscheidet bei JEDEM Start
    // (Festlegung (e)) — auch bei einer Kennung, die schon bestaetigt war:
    // sie ueberlebt die Anmeldung eines ANDEREN Benutzers am selben Geraet
    // (REQ-DM-070 (e)).
    pruefeMandantenwahl();
    if (selectedCompanyId.value !== gewaehlt) {
      _bestaetigteWahl = null;
      return meldeVerdikt('nicht-im-anspruch', gewaehlt);
    }

    // Schon in dieser Sitzung gelesen — kein zweiter Lesevorgang und keine
    // zweite Zeile: protokolliert werden UEBERGAENGE, nicht Zustaende
    // (REQ-OPS-055 (e)). Eine Zeile je Navigation flutete die Konsole.
    if (_bestaetigteWahl === gewaehlt) return 'bestaetigt';

    const ausgang = await mitFrist(
      mitAnspruchsWiederholung('companies', () => fetchCompanyById(gewaehlt))
        .catch((err) => {
          console.error('[CompanyStore] Gewaehlter Mandant nicht lesbar:', gewaehlt, err);
          return null;
        }),
      fristMs,
    );

    if (ausgang.abgelaufen) return meldeVerdikt('frist-abgelaufen', gewaehlt);
    if (ausgang.wert === null) return meldeVerdikt('nicht-lesbar', gewaehlt);

    _bestaetigteWahl = gewaehlt;
    return meldeVerdikt('bestaetigt', gewaehlt);
  }

  /**
   * Die Mandanten des Anspruchs — JE KENNUNG EINZELN gelesen (REQ-SEC-029 (e)).
   *
   * ⚠ **KEINE ABFRAGE UND KEIN ABO.** Die Rules erlauben einem Mitglied
   * `companies/{id}` allein als `get`, nie als Liste: eine Abfrage ohne
   * `companyId`-Filter — und die Mandantenliste kann von Natur aus keinen
   * tragen — wird als GANZES abgewiesen und nicht etwa gekuerzt. Ein
   * `onSnapshot` auf die Sammlung liefe deshalb bei jedem `user` in
   * `permission-denied`, fuenfmal wiederaufgenommen, und endete im
   * Fehlzustand — die Mandantenwahl bliebe leer.
   *
   * Fehlende und nicht lesbare Dokumente fallen still weg: ein Anspruch kann
   * eine Kennung tragen, deren Mandant nicht (mehr) besteht (die Grenze aus
   * dem Ausgangstor T-M30.1, Punkt (iii)); das ist kein Fehlzustand der Liste.
   */
  async function leseMandantenAusAnspruch(ids: string[]): Promise<void> {
    // Die Nummer dieser Lesung (T-M47.2): wird inzwischen neu abonniert oder
    // abgemeldet, darf ihr spaetes Ergebnis die Liste NICHT mehr ueberschreiben
    // — das ist das Abraeumen des vorigen Abos fuer den Weg ohne Listener
    // (REQ-OPS-058 Praez. 4 (b)).
    const lesung = _aboNummer;
    isLoading.value = true;
    resetListe();
    isSubscribed.value = false;
    try {
      const geladen = await Promise.all(
        ids.map(async (id) => {
          try {
            return await mitAnspruchsWiederholung('companies', () => fetchCompanyById(id));
          } catch (err) {
            console.error('[CompanyStore] Mandant nicht lesbar:', id, err);
            return null;
          }
        }),
      );
      if (lesung !== _aboNummer) return;   // ueberholt — der neuere Weg fuehrt
      companies.value = geladen
        .filter((c): c is Company => c !== null)
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      if (_fehlerQuelle === 'companies') { error.value = null; _fehlerQuelle = null; }
      antwortLiegtVor.value = true;
    } finally {
      if (lesung === _aboNummer) isLoading.value = false;
    }
  }

  // ── DIE WEICHE FAELLT NACH DEM ANSPRUCH (T-M47.2, REQ-OPS-058 Praez. 4) ──────
  /**
   * Hat ein Aufrufer die Mandantenliste verlangt (`subscribe()`) und sie seither
   * nicht abgemeldet (`unsubscribe()`)? Nur dann loest ein geaenderter Anspruch
   * ein neues Abo aus — der Beobachter unten ist KEINE zweite Abo-Quelle, er
   * fuehrt allein den Wunsch eines Aufrufers nach.
   */
  let _aboGewuenscht = false;
  /**
   * Der Anspruch, unter dem das laufende Abo seine Weiche gestellt hat
   * (`null`: noch keine Weiche — der Anspruch war beim Aufruf ungelesen).
   */
  let _aboAnspruch: string | null = null;
  /**
   * Laufende Nummer des Abos; jedes `subscribe()`/`unsubscribe()` erhoeht sie
   * und entwertet eine noch laufende Einzellesung.
   */
  let _aboNummer = 0;

  /**
   * Der Anspruch als Vergleichswert — `null`, solange er nicht gelesen ist.
   * Die Kennungen werden sortiert: dieselbe Menge in anderer Reihenfolge ist
   * kein neuer Anspruch und soll kein neues Abo ausloesen.
   */
  function anspruchsSchluessel(): string | null {
    const authStore = useAuthStore();
    if (!authStore.anspruchGelesen) return null;
    const ids = [...authStore.companyIds].sort().join(',');
    return `${authStore.isAdmin ? 'admin' : 'user'}|${ids}`;
  }

  /**
   * DER BEOBACHTER AUF DEN ANSPRUCH — dieselbe Bauform wie T-M34.4
   * (REQ-OPS-058 Praez. 4 (a)/(b)).
   *
   * ⚠ **DER BEFUND, DEN ER SCHLIESST (OP-235, belegt am 2026-10-05 in
   * `test/firmenwahlLeerzustandNachAnspruch.test.ts`):** bei der FRISCHEN
   * Anmeldung ist der Anmeldestart schon beendet (`isLoading === false` aus
   * dem Durchgang „nicht angemeldet"), wenn `verarbeiteAnmeldezustand`
   * `firebaseUser` setzt — der Watcher in `App.vue` (`authed && !loading`)
   * ruft `subscribe()` deshalb VOR `start.claimRead`. Bis dahin stellte
   * `subscribe()` die Weiche sofort an `isAdmin` — ohne Anspruch `false`, auch
   * fuer einen Admin — und las die leere `companyIds`-Liste: „Noch keine
   * Firmen angelegt.". Danach aendert sich weder `isAuthenticated` noch
   * `isLoading`; niemand las nach, erst ein Neuladen zeigte die Firma.
   *
   * **Kein Warten mit fester Frist und keine zweite Rollenweiche:** die Weiche
   * steht weiterhin allein in `subscribe()`; der Beobachter ruft es nur erneut,
   * wenn sich der Anspruch gegenueber dem Stand des laufenden Abos geaendert
   * hat. `subscribe()` raeumt das vorige Abo ab, bevor es ein neues setzt.
   */
  watch(anspruchsSchluessel, (neu) => {
    if (!_aboGewuenscht) return;
    if (neu === _aboAnspruch) return;
    subscribe();
  });

  /**
   * Die Mandantenliste — ZWEIGLEISIG seit T-M30.7 (REQ-SEC-029 (e)).
   *
   *   • **Admin:** Live-Abo ALLER Firmen, ohne `limit` (REQ-OPS-058 (1)) —
   *     unveraendert der Weg von vorher.
   *   • **`user`:** die Mandanten des Anspruchs, JE KENNUNG EINZELN gelesen.
   *     Kein Abo: die Rules weisen die Liste ab (siehe
   *     {@link leseMandantenAusAnspruch}).
   *
   * Die Weiche liegt HIER und nicht an den siebzehn Aufrufstellen: `subscribe()`
   * wird aus jeder Ansicht gerufen, die die Liste braucht, und keine davon soll
   * die Rollenfrage kennen muessen.
   *
   * ⚠ **DIE WEICHE FAELLT ERST NACH DEM GELESENEN ANSPRUCH** (T-M47.2,
   * REQ-OPS-058 Praez. 4 (a)/(c)). *Berichtigt statt geloescht (REQ-OPS-036
   * Praez. 6): bis zum 2026-10-05 stellte `subscribe()` sie bei JEDEM Aufruf
   * sofort an `authStore.isAdmin` — vor dem Anspruch nahm damit auch ein Admin
   * den `user`-Weg mit leerer Kennungsliste (OP-235).* Ist der Anspruch beim
   * Aufruf noch nicht gelesen, merkt sich der Store den Wunsch, zeigt den
   * LADEZUSTAND und stellt die Weiche, sobald der Beobachter oben den Anspruch
   * meldet. Eine spaetere Aenderung des Anspruchs abonniert ebenso neu (b).
   */
  function subscribe() {
    _unsub?.();  // Kill any existing listener before creating a new one
    _unsub = null;
    _aboNummer += 1;
    _aboGewuenscht        = true;
    antwortLiegtVor.value = false;
    pruefeMandantenwahl();
    const authStore = useAuthStore();
    _aboAnspruch = anspruchsSchluessel();
    if (_aboAnspruch === null) {
      // (a)/(c): noch keine Weiche und keine Aussage — nur der Ladezustand.
      isLoading.value    = true;
      isSubscribed.value = false;
      resetListe();
      return;
    }
    if (!authStore.isAdmin) {
      void leseMandantenAusAnspruch(authStore.companyIds);
      return;
    }
    isLoading.value = true;
    resetListe();
    const q = query(
      collection(db, 'companies'),
      ...companiesQueryConstraints(),
    );
    isSubscribed.value = true;
    // Fehlerweg, Protokoll und Rueckzugsstaffel kommen aus der EINEN
    // Bildungsstelle (REQ-OPS-053 (e), T-M21.4).
    _unsub = liveAbo<QuerySnapshot>({
      collection: 'companies',
      ziel: () => q,
      onData: (snap) => {
        companies.value = snap.docs.map(d => ({ id: d.id, ...d.data() } as Company));
        isLoading.value = false;
        antwortLiegtVor.value = true;
      },
      // Der Zustand bleibt WAHR (OP-151): das „abonniert"-Merkmal wird
      // zurueckgenommen. Ohne gewaehlten Mandanten bleibt das ganze Dashboard
      // verschlossen — eine tote Mandantenliste, die wie eine geladene leere
      // aussieht, ist hier besonders teuer.
      onFehler: (err) => {
        error.value        = err.message;
        _fehlerQuelle      = 'companies';
        isLoading.value    = false;
        isSubscribed.value = false;
        antwortLiegtVor.value = true;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung) — und er nimmt NUR die
      // eigene Meldung zurueck.
      onErholt: () => {
        if (_fehlerQuelle === 'companies') { error.value = null; _fehlerQuelle = null; }
        isSubscribed.value = true;
      },
      onStill: () => {
        isLoading.value    = false;
        isSubscribed.value = false;
        antwortLiegtVor.value = true;
      },
    });
  }

  function unsubscribe() {
    _unsub?.();
    _unsub             = null;
    isSubscribed.value = false;
    // T-M47.2: der Wunsch faellt mit — ein spaeter gelesener Anspruch startet
    // nach der Abmeldung kein Abo mehr, und eine laufende Einzellesung ist
    // entwertet.
    _aboGewuenscht        = false;
    _aboAnspruch          = null;
    _aboNummer           += 1;
    antwortLiegtVor.value = false;
  }

  // ── Select ─────────────────────────────────────────────────
  function selectCompany(id: string) {
    selectedCompanyId.value = id;
    localStorage.setItem('pf_company_id', id);
  }

  function clearCompany() {
    selectedCompanyId.value = null;
    localStorage.removeItem('pf_company_id');
    stopMemberSubscription();
    members.value = [];
    error.value   = null;  // reset stale errors
    _fehlerQuelle = null;
  }

  // ── CRUD ───────────────────────────────────────────────────
  // `createdAt`/`updatedAt` setzt der Server (serverTimestamp), nicht der Aufrufer.
  async function addCompany(data: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) {
    const authStore = useAuthStore();
    await addDoc(collection(db, 'companies'), {
      ...data,
      createdAt: serverTimestamp(),
      createdBy: authStore.firebaseUser?.uid ?? '',
    });
  }

  async function updateCompany(id: string, data: Partial<Omit<Company, 'id'>>) {
    await updateDoc(doc(db, 'companies', id), data);
  }

  /**
   * Liest EINEN Mandanten nach — ohne Abo und ohne die Liste zu beruehren.
   *
   * ⚠ **IHR ZWECK HAT AM 2026-09-01 GEWECHSELT (T-M30.11)** — berichtigt statt
   * geloescht (REQ-OPS-036 Praez. 6). **Bis dahin stand hier:** „(REQ-DM-036
   * Praez. 1/2, T-M29.4). WOFUER: die Kollisionsmeldung der Drucker-Kennung
   * nennt den Mandanten des belegenden Druckers. Der Treffer kommt aus einer
   * SYSTEMWEITEN Abfrage (`printerStore.findByPayfixPrinterId`, REQ-DM-035) und
   * kann deshalb in einem Mandanten liegen, der in `companies` gar nicht
   * geladen ist — die Liste taugt als Quelle also nicht."
   *
   * **BEIDES TRIFFT NICHT MEHR ZU.** Die Kollisionsmeldung ruft sie nicht mehr:
   * die Auskunft ueber eine belegte Kennung kommt seit T-M30.11 vom Server
   * (REQ-SEC-034, REQ-DM-036 Praez. 3 (iii)), und die systemweite Abfrage im
   * `printerStore` besteht nicht mehr.
   *
   * **WOFUER SIE HEUTE DA IST:** `leseMandantenAusAnspruch` liest die Mandanten
   * des Anspruchs JE KENNUNG EINZELN nach (REQ-SEC-029 (e), T-M30.7) — die
   * Rules erlauben `companies/{id}` einem Mitglied allein als `get`, nie als
   * Liste.
   *
   * GENAU EIN `getDoc`, und „Dokument fehlt" ist ein eigener ZWEIG, kein
   * Fehlerpfad: `null` heisst „es gibt kein solches Dokument". Der leere
   * Aufruf wird hier ein zweites Mal abgefangen (Praez. 2 (iii)) — den
   * Lesevorgang laesst schon der Aufrufer entfallen; ein `doc(db,'companies','')`
   * wuerde werfen und die Meldung verschlucken, um derentwillen er ruft.
   */
  async function fetchCompanyById(id: string): Promise<Company | null> {
    const key = (id ?? '').trim();
    if (!key) return null;
    const snap = await getDoc(doc(db, 'companies', key));
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as Company) : null;
  }

  async function deleteCompany(id: string) {
    if (selectedCompanyId.value === id) clearCompany();
    await deleteDoc(doc(db, 'companies', id));
  }

  // ── Member Subscription ────────────────────────────────────
  function subscribeMembers(companyId: string) {
    if (!companyId || companyId === 'new') return;   // guard against undefined/new
    stopMemberSubscription();
    membersLoading.value = true;
    isMembersSubscribed.value = true;
    _memberUnsub = liveAbo<QuerySnapshot>({
      collection: 'members',
      path:       `companies/${companyId}/members`,
      companyId,
      ziel: () => collection(db, 'companies', companyId, 'members'),
      onData: snap => {
        members.value = snap.docs.map(d => ({ ...d.data() } as CompanyMember));
        membersLoading.value = false;
      },
      onFehler: (err) => {
        console.error('[CompanyStore] Member subscription error:', err);
        error.value               = err.message ?? 'Fehler beim Laden der Mitglieder.';
        _fehlerQuelle             = 'members';
        membersLoading.value      = false;
        isMembersSubscribed.value = false;
      },
      onErholt: () => {
        if (_fehlerQuelle === 'members') { error.value = null; _fehlerQuelle = null; }
        isMembersSubscribed.value = true;
      },
      onStill: () => {
        membersLoading.value      = false;
        isMembersSubscribed.value = false;
      },
    });
  }

  function stopMemberSubscription() {
    _memberUnsub?.();
    _memberUnsub              = null;
    isMembersSubscribed.value = false;
  }

  // ── Member CRUD ────────────────────────────────────────────
  async function addMember(companyId: string, member: Omit<CompanyMember, 'addedAt'>) {
    await setDoc(
      doc(db, 'companies', companyId, 'members', member.userId),
      { ...member, addedAt: serverTimestamp() },
    );
  }

  // ⚠ ENTFALLEN AM 2026-09-01 (T-M30.7, REQ-DM-070 (d)):
  // `updateMemberPermission(companyId, userId, permission)`. Das Feld
  // `permission` (`edit` | `read`) war ein TOTES DATENFELD — geschrieben und
  // angezeigt, aber an keiner Stelle ausgewertet: weder in den Rules, noch in
  // den Cloud Functions, noch im Dashboard. *Ein Feld, das aussieht, als steuere
  // es Rechte, und keine steuert, ist gefaehrlicher als kein Feld.* Der
  // Feldvertrag der Mitgliedschaft ist abschliessend `userId`, `displayName`,
  // `email`, `addedAt` (REQ-DM-070 (c)); die Rechte haengen an der Rolle
  // (REQ-SEC-027) und an der Mitgliedschaft selbst.
  // *Berichtigt statt geloescht — REQ-OPS-036 Praez. 6.*

  async function removeMember(companyId: string, userId: string) {
    await deleteDoc(doc(db, 'companies', companyId, 'members', userId));
  }

  /**
   * BESTEHT EINE MITGLIEDSCHAFT? — ein `getDoc`, ohne Abo (T-M30.6).
   *
   * WOFUER: die Benutzerverwaltung zeigt die Mandanten EINES Benutzers und
   * laesst sie setzen und entziehen (REQ-SEC-027 (a)). Die Tatsache steht in
   * `companies/{id}/members/{uid}` — verteilt ueber die Mandanten, nicht am
   * Benutzer.
   *
   * ⚠ **WARUM KEINE SAMMELGRUPPEN-ABFRAGE** — also keine Abfrage ueber die
   *   Sammlungsgruppe `members` mit einem Gleichheitsfilter auf das Feld
   *   `userId` (hier ABSICHTLICH in Worten und nicht als Code geschrieben: der
   *   Index-Waechter `test/firestoreIndexes.test.ts` erhebt Abfrageformen aus
   *   `dashboard/src` und liest Kommentare NICHT als Kommentare) —, obwohl sie
   *   die naheliegende Form waere: sie
   *   braucht den Einzelfeld-Index mit dem Bereich Sammlungsgruppe, und dessen
   *   Zustand im Zielprojekt ist am 2026-09-01 **nicht abgelesen** — die
   *   Sichtpruefung steht als Auflage A1 am Rollout-Task T-M30.12 noch aus
   *   (REQ-OPS-039 (a)–(c); der Emulator erzwingt ihn nicht, REQ-DM-050a).
   *   *Eine Ansicht auf einen Index zu setzen, von dem niemand gemessen hat,
   *   dass er „Aktiviert" ist, heisst, sie am Rollout-Abend leer zu sehen.*
   *   Zweitens traegt eine solche Abfrage keinen `companyId`-Filter und ist
   *   damit genau die Form, die REQ-SEC-029 (e) (i) meidet.
   *
   * **BENANNTE GRENZE:** der Aufrufer stellt je Mandant eine Anfrage — bei N
   * Mandanten N Lesevorgaenge, einmal beim Oeffnen der Benutzer-Detailseite.
   * Das ist die Flaeche eines Admins und keine Betriebsansicht; sie waechst mit
   * der Zahl der Mandanten und ist hier bewusst hingenommen.
   */
  async function fetchMember(companyId: string, userId: string): Promise<CompanyMember | null> {
    const cid = (companyId ?? '').trim();
    const uid = (userId ?? '').trim();
    if (!cid || !uid) return null;
    const snap = await getDoc(doc(db, 'companies', cid, 'members', uid));
    return snap.exists() ? ({ ...snap.data() } as CompanyMember) : null;
  }

  return {
    companies, isLoading, error,
    selectedCompanyId, selectedCompany, hasCompany,
    members, membersLoading, isSubscribed, isMembersSubscribed, antwortLiegtVor,
    subscribe, unsubscribe,
    selectCompany, clearCompany,
    addCompany, updateCompany, deleteCompany, fetchCompanyById,
    subscribeMembers, stopMemberSubscription,
    addMember, removeMember, fetchMember,
    pruefeMandantenwahl, bestaetigeGespeicherteWahl,
  };
});
